import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { LocalePickerDialogComponent } from '@modules/locale/locale-picker-dialog/locale-picker-dialog.component';
import { Store } from '@ngrx/store';
import { User } from '@pixacare/domain';
import { AuthAppRedirectService } from '@services/auth-app-redirect.service';
import { DialogService } from '@services/dialog.service';
import { filter } from 'rxjs';
import { selectHasActiveVideoUploads } from '@shared/store/video-uploads/video-uploads.selectors';
import { Status } from 'src/app/shared/models/enums/status.enum';
import { ActionsService } from 'src/app/shared/models/helpers/actions-service';
import { MenuAction } from 'src/app/shared/models/menu-actions/menu-action';
import { MenuActionId } from 'src/app/shared/models/menu-actions/menu-action-id.enum';

@Injectable()
export class UserActionsService extends ActionsService {
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authAppRedirectService = inject(AuthAppRedirectService);
  private readonly store = inject(Store);

  private readonly hasActiveVideoUploads = this.store.selectSignal(selectHasActiveVideoUploads);

  actions: MenuAction<User>[] = [
    {
      id: MenuActionId.USER_PROFILE,
      label: this.translocoService.translate('Mon profil'),
      icon: '@tui.user',
      execute: (): void => {
        this.authAppRedirectService.openAccount();
      },
    },
    {
      id: MenuActionId.USER_EXPORTS,
      label: this.translocoService.translate('Mes exports'),
      icon: '@tui.archive',
      execute: (): void => {
        this.router.navigate(['/exports']);
      },
    },
    {
      id: MenuActionId.USER_INVITATIONS,
      label: this.translocoService.translate('Mes invitations'),
      icon: '@tui.bell',
      execute: (): void => {
        this.router.navigate(['/invitations']);
      },
    },
    {
      id: MenuActionId.USER_LANGUAGE,
      label: this.translocoService.translate('Langue'),
      icon: '@tui.globe',
      execute: (): void => {
        this.dialogService
          .openComponent(LocalePickerDialogComponent, {
            label: this.translocoService.translate('Langue'),
            size: 's',
          })
          .subscribe();
      },
    },
    {
      id: MenuActionId.USER_LOGOUT,
      label: this.translocoService.translate('Déconnexion'),
      status: Status.ERROR,
      icon: '@tui.log-out',
      execute: (): void => {
        if (!this.hasActiveVideoUploads()) {
          this.authAppRedirectService.logout();
          return;
        }
        // A running upload can't survive leaving the app, so confirm before logging out.
        this.dialogService
          .openConfirm(this.translocoService.translate('Un envoi de fichiers est en cours'), {
            content: this.translocoService.translate(
              "Si vous quittez l'application, l'envoi en cours sera interrompu. Mieux vaut attendre qu'il se termine.",
            ),
            yes: this.translocoService.translate('Quitter quand même'),
            no: this.translocoService.translate('Rester'),
          })
          .pipe(filter(Boolean))
          .subscribe(() => this.authAppRedirectService.logout());
      },
    },
  ];
}
